import { Buffer } from 'buffer';
import { Platform } from 'react-native';
import { dynamicallyLoadScript } from '../../../utils/loadScript';

export const isHEICFileType = (contentType: string) => contentType === 'image/heic' || contentType === 'image/heif';

const convert = async (base64Data: string, contentType: string) => {
    if (Platform.OS === 'web') {
        const uint8Array = convertBase64ToUInt8Array(base64Data);

        const blob = new Blob([uint8Array as BlobPart], { type: contentType });

        // this is an extremely large library, so we dynamically load it.  this will not work on native, but it may be able to handle heic natively
        await dynamicallyLoadScript('https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js');
        let pngBlob = await (window as any).heic2any({
            blob: blob,
            toType: 'image/png',
        });

        // get single png blob if heic2any returns an array
        if (Array.isArray(pngBlob)) {
            pngBlob = pngBlob[0];
        }

        // return base 64 data
        return extractBase64FromBlob(pngBlob);
    } else {
        return base64Data;
    }
};

export async function convertHEICToPNG(uri: string, contentType: string, originalName: string | null) {
    const convertedUri = await convert(uri, contentType);

    let updatedName = originalName || undefined;
    // remove the extension from the original name if it is .heic or .heif
    if (updatedName && (updatedName.endsWith('.heic') || updatedName.endsWith('.heif'))) {
        updatedName = updatedName.slice(0, updatedName.length - 5);
    }

    if (updatedName && !updatedName.endsWith('.jpg') && !updatedName.endsWith('.jpeg')) {
        updatedName += '.jpg';
    }

    return {
        name: `${updatedName || new Date().toISOString()}.jpg`,
        contentType: 'image/jpeg',
        uri: convertedUri,
        base64: maybeExtractBase64FromUri(convertedUri),
        s3UploadBuffer: Buffer.from(maybeExtractBase64FromUri(convertedUri), 'base64'),
        originalName: updatedName,
    };
}

function maybeExtractBase64FromUri(uri: string): string {
    return uri.startsWith('data:') ? uri.split(',')[1] : uri;
}

function convertBase64ToUInt8Array(base64: string): Uint8Array {
    // this is basically a node buffer
    // can get an array buffer with bytes.buffer
    // remove data:image/jpeg;base64, from base64 string if present
    const formatted = maybeExtractBase64FromUri(base64);

    const binary_string = window.atob(formatted);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binary_string.charCodeAt(i);
    }

    return bytes;
}

async function extractBase64FromBlob(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => resolve(String(reader.result));
        reader.onerror = reject;

        reader.readAsDataURL(blob);
    });
}
